import { useEffect, useRef, useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../lib/shadcn/card'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { Switch } from '../../lib/shadcn/switch'
import { Badge } from '../../lib/shadcn/badge'
import { Separator } from '../../lib/shadcn/separator'
import { Bold, Italic, Link2, List, Pilcrow, Save } from 'lucide-react'
import { toast } from '../../lib/shadcn/sonner'
import {
  useGetTrackingEmailSettings,
  useUpdateTrackingEmailSettings,
} from '../../hooks/backend/settingsPage'
import { HelpText } from '../../components/HelpText'

interface Placeholder {
  token: string
  description: string
}

interface Settings {
  enabled: boolean
  subject: string
  body: string
  placeholders: Placeholder[]
}

export default function TrackingEmailSettings() {
  const getSettings = useGetTrackingEmailSettings()
  const updateSettings = useUpdateTrackingEmailSettings()

  const [enabled, setEnabled] = useState(true)
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [placeholders, setPlaceholders] = useState<Placeholder[]>([])
  const [loaded, setLoaded] = useState(false)

  const bodyRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    void (async () => {
      const res = (await getSettings.trigger()) as Settings | undefined
      if (res) {
        setEnabled(res.enabled)
        setSubject(res.subject)
        setBody(res.body)
        setPlaceholders(res.placeholders ?? [])
        setLoaded(true)
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Insert text into the body at the cursor, or wrap the current selection.
  const applyToBody = (before: string, after = '') => {
    const el = bodyRef.current
    if (!el) {
      setBody(prev => prev + before + after)
      return
    }
    const start = el.selectionStart
    const end = el.selectionEnd
    const selected = body.slice(start, end)
    const next = body.slice(0, start) + before + selected + after + body.slice(end)
    setBody(next)
    // Restore a sensible cursor position after React re-renders.
    requestAnimationFrame(() => {
      el.focus()
      const caret = start + before.length + selected.length + after.length
      el.setSelectionRange(caret, caret)
    })
  }

  const insertPlaceholder = (token: string) => applyToBody(`{{${token}}}`)

  const handleSave = async () => {
    if (!subject.trim()) {
      toast.error('Subject is required')
      return
    }
    if (!body.trim()) {
      toast.error('Body is required')
      return
    }
    try {
      await updateSettings.trigger({ enabled, subject: subject.trim(), body })
      toast.success('Tracking email template saved')
    } catch (error) {
      toast.error(
        `Failed to save: ${error instanceof Error ? error.message : 'Unknown error'}`,
      )
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Shipment Tracking Email</CardTitle>
        <CardDescription><HelpText>
          The email offered to customers when an order ships. Edit the text and formatting,
          and insert placeholders that merge in order details.
        </HelpText></CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex items-center justify-between rounded-md border p-3">
          <div className="space-y-0.5">
            <Label htmlFor="tracking-email-enabled">Offer this email after shipping</Label>
            <p className="text-xs text-muted-foreground">
              When on, a “Email tracking to customer” option appears for shipped orders.
            </p>
          </div>
          <Switch
            id="tracking-email-enabled"
            checked={enabled}
            onCheckedChange={setEnabled}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="tracking-email-subject">Subject</Label>
          <Input
            id="tracking-email-subject"
            value={subject}
            onChange={e => setSubject(e.target.value)}
            placeholder="Your order {{order_number}} has shipped!"
          />
        </div>

        <div className="space-y-1.5">
          <Label>Placeholders</Label>
          <p className="text-xs text-muted-foreground">
            Click to insert into the body. They merge in real values when the email is sent.
          </p>
          <div className="flex flex-wrap gap-1.5">
            {placeholders.map(p => (
              <Badge
                key={p.token}
                variant="secondary"
                title={p.description}
                className="cursor-pointer hover:bg-secondary/70 font-mono text-xs"
                onClick={() => insertPlaceholder(p.token)}
              >
                {`{{${p.token}}}`}
              </Badge>
            ))}
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="tracking-email-body">Body (HTML)</Label>
          <div className="flex items-center gap-1 rounded-md border bg-muted/40 p-1">
            <Button type="button" variant="ghost" size="icon" className="h-7 w-7" aria-label="Bold"
              onClick={() => applyToBody('<strong>', '</strong>')}>
              <Bold className="h-3.5 w-3.5" />
            </Button>
            <Button type="button" variant="ghost" size="icon" className="h-7 w-7" aria-label="Italic"
              onClick={() => applyToBody('<em>', '</em>')}>
              <Italic className="h-3.5 w-3.5" />
            </Button>
            <Button type="button" variant="ghost" size="icon" className="h-7 w-7" aria-label="Link"
              onClick={() => applyToBody('<a href="https://">', '</a>')}>
              <Link2 className="h-3.5 w-3.5" />
            </Button>
            <Button type="button" variant="ghost" size="icon" className="h-7 w-7" aria-label="Paragraph"
              onClick={() => applyToBody('<p>', '</p>')}>
              <Pilcrow className="h-3.5 w-3.5" />
            </Button>
            <Button type="button" variant="ghost" size="icon" className="h-7 w-7" aria-label="Bullet list"
              onClick={() => applyToBody('<ul>\n  <li>', '</li>\n</ul>')}>
              <List className="h-3.5 w-3.5" />
            </Button>
          </div>
          <textarea
            id="tracking-email-body"
            ref={bodyRef}
            value={body}
            onChange={e => setBody(e.target.value)}
            spellCheck
            className="min-h-[180px] w-full rounded-md border bg-background p-3 font-mono text-sm outline-none focus-visible:ring-1 focus-visible:ring-ring"
            placeholder="<p>Hi {{first_name}}, your order has shipped…</p>"
          />
        </div>

        <div className="space-y-1.5">
          <Label>Preview</Label>
          <p className="text-xs text-muted-foreground">
            Formatting preview — placeholders are shown literally here and merged at send time.
          </p>
          <div
            className="rounded-md border bg-background p-4 text-sm [&_a]:text-primary [&_a]:underline [&_ul]:list-disc [&_ul]:pl-5 [&_p]:my-2"
            // The template is authored by trusted org admins in Settings.
            dangerouslySetInnerHTML={{ __html: body }}
          />
        </div>

        <Separator />

        <div className="flex justify-end">
          <Button
            onClick={() => void handleSave()}
            disabled={!loaded || updateSettings.loading}
            className="gap-1.5"
          >
            <Save className="h-4 w-4" />
            {updateSettings.loading ? 'Saving…' : 'Save Template'}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
